import React, { useState } from 'react';
import { Box, Container, Typography, styled, Stack, Chip, Button, Tabs, Tab } from '@mui/material';
import Grid from '@mui/material/Grid'; // Using Grid2 for better performance
import { motion, AnimatePresence, m } from 'framer-motion';

// Icons
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import CloudIcon from '@mui/icons-material/Cloud';
import DirectionsIcon from '@mui/icons-material/Directions';
import BarcodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import TerminalIcon from '@mui/icons-material/Terminal';

// --- Animated Background Elements ---
const BackgroundHero = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    background: '#020617',
    overflow: 'hidden',
});

const Blob = styled(motion.div)(({ color, size, top, left }) => ({
    position: 'absolute',
    width: size,
    height: size,
    background: color,
    filter: 'blur(100px)',
    borderRadius: '50%',
    opacity: 0.15,
    top: top,
    left: left,
    zIndex: 0,
}));

// --- Modern Card Styling ---
const ProjectCard = styled(motion.div)({
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '28px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(12px)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    '&:hover': {
        borderColor: 'rgba(45, 212, 191, 0.5)',
        background: 'rgba(255, 255, 255, 0.06)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.4), 0 0 20px rgba(45, 212, 191, 0.15)',

    }
});

const ImageBox = styled(Box)({
    width: '100%',
    height: '240px', // Thodi height badha di hai screenshoots ke liye
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Background mesh taaki contain wali image achi lage
    background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.2) 100%)',
    '& img': {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain', // Isse image poori dikhegi bina kate
        transition: 'transform 0.6s ease',
        borderRadius: '20px',
        padding: '10px', // Taki edges se chipke nahi
        marginTop: '10px',
    }
});

const CategoryTab = styled(Tab)({
    color: '#94a3b8',
    textTransform: 'none',
    fontWeight: 700,
    zIndex: 2,
    transition: '0.3s',
    '&.Mui-selected': {
        color: '#fff',
    }
});

const PortfolioProjects = () => {
    const [filter, setFilter] = useState('All');

    const projects = [
        {
            title: "Pratima Security Services Web",
            category: "Web",
            desc: "A specialized enterprise web solution for Pratima Security Services, the leading security provider in the Ghaziabad & Noida region. The platform is engineered with a conversion-focused UI, integrating EmailJS for direct client inquiries and Google Maps API for precise branch locating. It features a scalable service architecture designed to project professional authority and operational transparency in the high-stakes security industry.",
            tech: ["HTML5", "CSS3", "JavaScript", "Google Maps", "EmailJS"],
            icon: <LanguageIcon />,
            image: "Pratima_Security_Services.png"
        },

        {
            title: "KCKV India Web",
            category: "Web",
            desc: "A sophisticated digital showcase developed for KCKV India, a premier architecture and design firm in Delhi NCR. The platform is built on a Minimalist Design Language to let the architectural portfolios speak for themselves. Engineered with a Grid-based Responsive Architecture, it ensures that complex design blueprints and project high-resolution renders are displayed with pixel-perfect clarity across all devices, integrated with EmailJS for direct client consultations.",
            tech: ["HTML5", "CSS3", "JavaScript", "Google Maps", "EmailJS"],
            icon: <LanguageIcon />,
            image: "KCKV_India.png"
        },

        {
            title: "Third Eye Securitas Web",
            category: "Web",
            desc: "Developed a high-performance enterprise web portal for Third Eye Securitas, a premier security solutions provider. The platform features a clean, minimalist architecture designed to showcase high-stakes security services. It is optimized for high conversion with a fully responsive service ecosystem, ensuring a seamless professional experience for corporate clients across all device categories, from mobile to ultra-wide desktops.",
            tech: ["HTML5", "CSS3", "JavaScript", "Google Maps", "Firebase"],
            icon: <LanguageIcon />,
            image: "ThirdEyeSecuritas.png"
        },

        {
            title: "Third Eye Securitas Admin",
            category: "Android Native",
            desc: "A centralized administrative command center built with Jetpack Compose to streamline security service operations. It features a high-performance Real-time Client Management dashboard and an automated recruitment engine. The app integrates a custom In-app PDF Engine for seamless CV screening, allowing administrators to evaluate job applications and manage service inquiries with zero friction, backed by a real-time Firebase infrastructure.",
            tech: ["Kotlin", "Jetpack Compose", "Firebase", "PDF SDK"],
            icon: <PictureAsPdfIcon />,
            image: "third_eye_securitas.jpeg"
        },

        {
            title: "BrancoAttendence",
            category: "Android Native",
            desc:"A robust, enterprise-grade Workforce Management Solution developed for Brancosoft Pvt Ltd. The system features a high-precision Geo-fencing Attendance Engine that validates employee presence based on real-time location coordinates. Engineered with Android WorkManager for reliable background data synchronization and seamless REST API integration, it automates the end-to-end attendance lifecycle, ensuring operational transparency and eliminating manual tracking errors.",
            tech:["Java","Geolocation","Work Manager","RestAPIs"],
            icon:<TerminalIcon/>,
            image:"BrancoAttendence.jpeg"
        },

        {
            title: "Nextra AI Assistant",
            category: "Flutter",
            desc: "A comprehensive AI-driven utility ecosystem built with Flutter, featuring an advanced Speech-to-Speech interface for natural interactions. Integrated Google ML Kit for high-accuracy OCR (Optical Character Recognition) and intelligent QR management. Beyond AI, the app includes professional-grade Network Testing tools, making it a versatile assistant for both productivity and technical diagnostics.",
            tech: ["Flutter","Firebase", "ML Kit", "AI API"],
            icon: <SmartToyIcon />,
            image: "nextra.jpeg"
        },
        {
            title:"Alarm Clock",
            category: "Android Native",
            desc:"A feature-rich Alarm Clock application developed using Java. The app offers customizable alarm, snooze options, and a user-friendly interface. It also includes additional features such as a stopwatch, timer, and world clock, making it a comprehensive time management tool for users.",
            tech:["Java","Work Manager","Alarm Manager"],
            icon:<DirectionsIcon/>,
            image:"Alarm-clock.png"
        },

        {
            title: "Quotify",
            category: "Android Native",
            desc: "A sleek and modern Quote application developed using Kotlin. Quotify offers a vast collection of inspirational quotes across various categories. Users can easily browse, favorite, and share quotes with friends. The app features a clean UI/UX design, offline access to quotes to keep users motivated.",
            tech: ["Kotlin", "JSON Data"],
            icon: <LocationOnIcon />,
            image: "Quotify.png"
        },

        {
            title: "File Management System",
            category: "Android Native",
            desc: "Developed an enterprise-grade File Management System designed for multi-branched organizations to bridge the gap between physical and digital document tracking. Integrated RFID Technology to enable real-time Physical File Location Tracking, automating the file allocation process and streamlining intra-branch File Requests. The system features a robust offline-first architecture using Room Database and cloud synchronization for seamless collaboration across different geographic locations.",
            tech: ["Java", "RestAPIs", "Room Database", "RFID"],
            icon: <CloudIcon />,
            image: "FMS.png"
        },

        {
            title: "Bettersleep IMS",
            category: "Android Native",
            desc: "A robust, industrial-grade Inventory Management System (IMS) engineered for real-time warehouse operations. Integrated with the Zebra RFID SDK, it enables bulk asset tracking and high-speed stock auditing. The system features a hybrid scanning engine (RFID + Barcode) and a high-concurrency SQLite/Room architecture to handle large-scale inventories. It streamlines the entire supply chain—from automated stock replenishment to precise order fulfillment—reducing human error by 90%.",
            tech: ["Java", "RestAPIs", "RFID", "Barcode Scanning", "SQLite", "Zebra RFID SDK"],
            icon: <BarcodeScannerIcon />,
            image: "Bettersleep_IMS.jpg"
        },

        {
            title: "macOS Desktop UI",
            category: "Flutter",
            desc: "A high-fidelity macOS Desktop Environment replication built entirely with Flutter. This project demonstrates advanced mastery over Custom Paint, intricate animations, and complex layout management. It features a fully functional Dynamic Dock, menu bar interactions, and window management, achieving pixel-perfect accuracy and smooth 60FPS transitions that mirror the native Apple experience.",
            tech: ["Flutter"],
            icon: <LanguageIcon />,
            image: "MacScreen.png"
        },
    ];

    const filteredProjects = filter === 'All'
        ? projects
        : projects.filter(p => p.category === filter);

    return (
        <Box sx={{ position: 'relative', minHeight: '100vh', py: 10 }}>
            {/* Animated Background */}
            <BackgroundHero>
                <Blob
                    color="#3b82f6" size="500px" top="-10%" left="-5%"
                    animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
                    transition={{ duration: 15, repeat: Infinity }}
                />
                <Blob
                    color="#2dd4bf" size="400px" top="40%" left="70%"
                    animate={{ x: [0, -40, 0], y: [0, 60, 0] }}
                    transition={{ duration: 20, repeat: Infinity }}
                />
            </BackgroundHero>

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header Section */}
                <Stack alignItems="center" spacing={2} sx={{ mb: 8 }}>
                    <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
                        <Typography variant="overline" sx={{ color: '#2dd4bf', fontWeight: 900, letterSpacing: 4 }}>
                            PORTFOLIO GRID
                        </Typography>
                    </motion.div>
                    <Typography variant="h2" sx={{
                        color: '#fff',
                        fontWeight: 900,
                        fontSize: { xs: '2.5rem', md: '4rem' },
                        textAlign: 'center'
                    }}>
                        Latest <span style={{ color: '#2dd4bf' }}>Creations</span>
                    </Typography>

                    <Tabs
                        value={filter}
                        onChange={(e, val) => setFilter(val)}
                        centered
                        TabIndicatorProps={{ style: { height: '100%', borderRadius: '12px', background: 'rgba(45, 212, 191, 0.15)', zIndex: 1 } }}
                        sx={{
                            mt: 4,
                            background: 'rgba(255,255,255,0.03)',
                            borderRadius: '16px',
                            p: 0.5,
                            border: '1px solid rgba(255,255,255,0.05)'
                        }}
                    >
                        {['All', 'Android Native', 'Flutter', 'Web'].map((cat) => (
                            <CategoryTab key={cat} label={cat} value={cat} />
                        ))}
                    </Tabs>
                </Stack>

                {/* Projects Grid */}
                <Grid container spacing={4}>
                    <AnimatePresence mode="popLayout">
                        {filteredProjects.map((project, idx) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={project.title}>
                                <ProjectCard
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.4, delay: idx * 0.1 }}
                                    whileHover={{ y: -12 }}
                                >
                                    <ImageBox className="img-container">
                                        <motion.img
                                            src={project.image}
                                            alt={project.title}
                                            whileHover={{ scale: 1.1 }}
                                        />
                                        {/* <Box sx={{
                                            position: 'absolute', top: 15, right: 15,
                                            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)',
                                            p: 0.8, borderRadius: '10px', display: 'flex'
                                        }}>
                                            <LaunchIcon sx={{ color: '#2dd4bf', fontSize: '1.2rem' }} />
                                        </Box> */}
                                    </ImageBox>

                                    <Box sx={{ p: 4, flexGrow: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#2dd4bf', fontWeight: 900, letterSpacing: 1.5 }}>
                                            {project.category}
                                        </Typography>
                                        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, mt: 1, mb: 2 }}>
                                            {project.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7, mb: 3 }}>
                                            {project.desc}
                                        </Typography>

                                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 4 }}>
                                            {project.tech.map(t => (
                                                <Chip
                                                    key={t} label={t}
                                                    sx={{
                                                        bgcolor: 'rgba(255,255,255,0.03)',
                                                        color: '#94a3b8',
                                                        border: '1px solid rgba(255,255,255,0.1)',
                                                        fontSize: '0.65rem', fontWeight: 700
                                                    }}
                                                />
                                            ))}
                                        </Stack>

                                        {/* <Button
                                            fullWidth
                                            variant="text"
                                            startIcon={<GitHubIcon />}
                                            sx={{
                                                justifyContent: 'space-between',
                                                color: '#fff',
                                                borderTop: '1px solid rgba(255,255,255,0.05)',
                                                pt: 2, borderRadius: 0,
                                                '&:hover': { color: '#2dd4bf' }
                                            }}
                                        >
                                            Source Code
                                        </Button> */}
                                    </Box>
                                </ProjectCard>
                            </Grid>
                        ))}
                    </AnimatePresence>
                </Grid>
            </Container>
        </Box>
    );
};

export default PortfolioProjects;

